/**
 * Komprese obrázků před uploadem (stejná idea jako Android PhotoUploadHandler:
 * max. delší strana 1920 px, JPEG 85 %).
 * Menší payload než raw soubor z foťáku → rychlejší upload a méně dat v MinIO.
 *
 * Těžká práce (decode / draw / encode) je rozložena přes await + toBlob,
 * aby main thread nezamrzl při velkých fotkách ze smartphonu.
 */

export const UPLOAD_MAX_EDGE_PX = 1920;
export const UPLOAD_JPEG_QUALITY = 0.85;

/** Uvolní main thread mezi těžkými kroky (komprese / multi-file fronta). */
export function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

function readFileAsDataUrlRaw(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) ?? '');
    reader.onerror = () => reject(new Error('Načtení souboru selhalo'));
    reader.readAsDataURL(file);
  });
}

function isCompressibleImageFile(file: File): boolean {
  if (!file.type.startsWith('image/')) return false;
  return file.type !== 'image/svg+xml';
}

/** U JPEG komprese upraví příponu na .jpg (např. přílohy deníku). */
export function uploadFileNameForCompressedImage(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '') || fileName;
  return `${base}.jpg`;
}

/**
 * Připraví soubor pro upload: obrázky (kromě SVG) vždy zkomprimuje na JPEG data URL,
 * ostatní typy pošle beze změny jako data URL.
 */
export async function fileToUploadDataUrl(file: File): Promise<string> {
  if (isCompressibleImageFile(file)) {
    return compressImageFileToJpegDataUrl(file);
  }
  return readFileAsDataUrlRaw(file);
}

/** Async JPEG encode — na rozdíl od sync `toDataURL` nepřidusí UI na celou dobu encode. */
function canvasToJpegDataUrl(canvas: HTMLCanvasElement, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('JPEG encode selhal'));
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) ?? '');
        reader.onerror = () => reject(new Error('Čtení JPEG selhalo'));
        reader.readAsDataURL(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Převede obrázek na JPEG data URL se zmenšením delší strany.
 * Nepodporovaný formát nebo chyba dekódování → původní data URL (např. HEIC na některých prohlížečích).
 * SVG se nechává beze změny (canvas by ho špatně zpracoval).
 */
export async function compressImageFileToJpegDataUrl(
  file: File,
  options?: { maxEdgePx?: number; quality?: number },
): Promise<string> {
  const maxEdgePx = options?.maxEdgePx ?? UPLOAD_MAX_EDGE_PX;
  const quality = options?.quality ?? UPLOAD_JPEG_QUALITY;

  if (!isCompressibleImageFile(file)) {
    return readFileAsDataUrlRaw(file);
  }

  try {
    await yieldToMain();
    const bitmap = await createImageBitmap(file);
    try {
      const iw = bitmap.width;
      const ih = bitmap.height;
      const scale = Math.min(1, maxEdgePx / Math.max(iw, ih, 1));
      const w = Math.max(1, Math.round(iw * scale));
      const h = Math.max(1, Math.round(ih * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return readFileAsDataUrlRaw(file);
      }
      await yieldToMain();
      ctx.drawImage(bitmap, 0, 0, w, h);
      await yieldToMain();
      const dataUrl = await canvasToJpegDataUrl(canvas, quality);
      if (!dataUrl.startsWith('data:image/jpeg')) {
        return readFileAsDataUrlRaw(file);
      }
      return dataUrl;
    } finally {
      bitmap.close();
    }
  } catch {
    return readFileAsDataUrlRaw(file);
  }
}
